/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from "react";
import { Box } from "@chakra-ui/react";
import lottie from "lottie-web";
import environment from "src/utils/environment";
import getAnimationData from "src/utils/getAnimationData";

function Lottie({
  options = {},
  eventListeners = [],
  controls = {},
  browserProps = { useTheme: true, hasRawData: false },
  width = "100%",
  height = "auto",
  animationName,
  animationOptions,
  animationData = getAnimationData(
    animationName,
    animationOptions,
    browserProps
  ),
  delay,
  getAnimControls,
  className,
  ...rest
}) {
  const animSource = useRef();
  const anim = useRef();
  const el = useRef();
  const timeoutRef = useRef();

  useEffect(() => {
    if (!animSource.current) animSource.current = animationData;
    if (animSource.current !== animationData) destroyAnimInstance();

    const defaultOptions = {
      container: el.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      ...(environment.isBrowser && !browserProps.hasRawData
        ? { path: animationData }
        : { animationData }),
      ...options
    };

    anim.current = lottie.loadAnimation(defaultOptions);
    eventListeners.forEach(({ event, callback }) => {
      if (!event || !callback) return;
      anim.current.addEventListener(event, callback);
    });

    if (getAnimControls) {
      getAnimControls({
        play: () => anim.current?.play(),
        stop: () => anim.current?.stop(),
        pause: () => anim.current?.pause(),
        setSpeed: (num) => anim.current?.setSpeed(num),
        setDirection: (num) => anim.current?.setDirection(num),
        playSegments: (array) => anim.current?.playSegments(array),
        useSubFrames: (bool) => anim.current?.useSubFrames(bool),
        goToAndStop: (num, bool) => anim.current?.goToAndStop(num, bool),
        goToAndPlay: (num, bool) => anim.current?.goToAndPlay(num, bool),
        getDuration: (bool) => anim.current?.getDuration(bool)
      });
    }

    animSource.current = animationData;
    return () => {
      destroyAnimInstance();
      clearTimeout(timeoutRef.current);
    };
  }, [animationName, animationData]);

  useEffect(() => {
    async function handleControlsUpdate() {
      if (delay && !timeoutRef.current) {
        await waitForDelay();
      }
      if (controls.shouldPlay) anim.current.play();
      if (controls.shouldStop) anim.current.stop();
      if (controls.shouldPause) anim.current.pause();

      if (controls.speed) anim.current.setSpeed(controls.speed);
      if (controls.direction) anim.current.setDirection(controls.direction);
      if (controls.segments) anim.current.playSegments(controls.segments);
      if (controls.useSubFrames)
        anim.current.useSubFrames(controls.useSubFrames);

      if (controls.goToAndStop) {
        anim.current.goToAndStop(
          controls.goToAndStop.value,
          controls.goToAndStop.isFrame
        );
      }
      if (controls.goToAndPlay) {
        anim.current.goToAndPlay(
          controls.goToAndPlay.value,
          controls.goToAndPlay.isFrame
        );
      }
    }
    if (anim.current) handleControlsUpdate();
  }, [controls, delay, anim.current]);

  async function waitForDelay() {
    anim.current.stop();
    return new Promise((resolve) => {
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        anim.current.play();
        resolve();
      }, delay);
    });
  }

  function destroyAnimInstance() {
    if (anim.current) {
      eventListeners.forEach((listener) => {
        anim.current.removeEventListener(listener[0], listener[1]);
      });
      anim.current.destroy();
    }

    anim.current = null;
  }

  if (!animationData) return null;

  return (
    <Box
      ref={el}
      width={width}
      height={height}
      className={className}
      {...rest}
    />
  );
}

export { Lottie, lottie };
